import { Col, Row } from "antd";
import { get, set } from "lodash";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  MdOutlineAddRoad,
  MdOutlineAlternateEmail,
  MdOutlineCall,
  MdOutlinePinDrop,
  MdOutlineSouthAmerica,
} from "react-icons/md";
import Alert from "shared/components/Alert";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import useAcorisZipAddress from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/useAcorisZipAddress";
import {
  ACORIS_ZIP_CODE_LENGTH,
  DEFAULT_ADDRESS_COUNTRY,
  getAcorisCityPlaceholder,
} from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/zipCode";
import { CONTACT_POINT_SYSTEM, SUBSCRIPTION_FORM_KEYS } from "utils/constants";

const Contact = ({ setPercent, isFieldRequired, initialValues, bindField }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });
  const { t: processesT } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const COUNTRIES = processesT("DETAIL.COUNTRIES", { returnObjects: true });
  const zipCodePath = `telecoms.contact.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`;
  const cityPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.CITY}.value`;
  const countryPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.COUNTRY}.value`;
  const zipCode = get(initialValues, zipCodePath);
  const country = get(initialValues, countryPath);
  const { isForeignZip, cities, citiesLoading } = useAcorisZipAddress(zipCode);

  useEffect(() => {
    if (isForeignZip || country) return;
    set(initialValues, countryPath, DEFAULT_ADDRESS_COUNTRY);
    setPercent(initialValues);
  }, [country, countryPath, initialValues, isForeignZip, setPercent]);

  const countryOptions = (Array.isArray(COUNTRIES) ? COUNTRIES : []).map(
    (name) => ({ label: name, value: name }),
  );

  const addressPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.ADDRESS}.value`;
  const addressField = bindField(addressPath, {
    label: SUBSCRIPTION.FORM.STREET_AND_INDEX.LABEL,
    required: isFieldRequired(addressPath),
    onBlur: () => setPercent(initialValues),
  });

  const addressLocationPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.ADDRESS_LOCATION}.value`;
  const addressLocationField = bindField(addressLocationPath, {
    label: SUBSCRIPTION.FORM.ADDRESS_LOCATION.LABEL,
    onBlur: () => setPercent(initialValues),
  });

  const complementPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.COMPLEMENT}.value`;
  const complementField = bindField(complementPath, {
    label: SUBSCRIPTION.FORM.COMPLEMENT.LABEL,
    onBlur: () => setPercent(initialValues),
  });

  const residencePath = `telecoms.contact.${CONTACT_POINT_SYSTEM.RESIDENCE}.value`;
  const residenceField = bindField(residencePath, {
    label: SUBSCRIPTION.FORM.RESIDENCE.LABEL,
    onBlur: () => setPercent(initialValues),
  });

  const zipCodeField = bindField(zipCodePath, {
    label: SUBSCRIPTION.FORM.POSTAL_CODE.LABEL,
    required: isFieldRequired(zipCodePath),
    disabled: true,
    onBlur: () => setPercent(initialValues),
  });

  const cityField = bindField(cityPath, {
    label: SUBSCRIPTION.FORM.CITY.LABEL,
    required: isFieldRequired(cityPath),
    onChange: () => {
      setPercent(initialValues);
    },
    onBlur: () => setPercent(initialValues),
  });

  const countryField = bindField(countryPath, {
    label: SUBSCRIPTION.FORM.COUNTRY.LABEL,
    required: isForeignZip || isFieldRequired(countryPath),
    onChange: () => {
      setPercent(initialValues);
    },
    onBlur: () => setPercent(initialValues),
  });

  const birthCountryPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`;
  const birthCountryField = bindField(birthCountryPath, {
    label: SUBSCRIPTION.FORM.BIRTH_COUNTRY.LABEL,
    required: isFieldRequired(birthCountryPath),
    onChange: () => {
      setPercent(initialValues);
    },
    onBlur: () => setPercent(initialValues),
  });

  const birthCityPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.BIRTH_CITY}.value`;
  const birthCityField = bindField(birthCityPath, {
    label: SUBSCRIPTION.FORM.BIRTH_CITY.LABEL,
    required: isFieldRequired(birthCityPath),
    onBlur: () => setPercent(initialValues),
  });

  const phonePath = `telecoms.contact.${CONTACT_POINT_SYSTEM.PHONE}.value`;
  const phoneField = bindField(phonePath, {
    label: SUBSCRIPTION.FORM.MOBILE.LABEL,
    required: isFieldRequired(phonePath),
    onBlur: () => setPercent(initialValues),
  });

  const landlinePath = `telecoms.contact.${CONTACT_POINT_SYSTEM.LANDLINE}.value`;
  const landlineField = bindField(landlinePath, {
    label: SUBSCRIPTION.FORM.LANDLINE.LABEL,
    onBlur: () => setPercent(initialValues),
  });

  const emailPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.EMAIL}.value`;
  const emailField = bindField(emailPath, {
    label: SUBSCRIPTION.FORM.EMAIL.LABEL,
    required: isFieldRequired(emailPath),
    onBlur: () => setPercent(initialValues),
  });

  const teletransmissionPath = "project.contact.fields.teletransmission";
  const teletransmissionField = bindField(teletransmissionPath, {
    label: SUBSCRIPTION.FORM.TELETRANSMISSION.LABEL,
    extractValue: (checked) => !checked,
    onChange: () => {
      setPercent(initialValues);
    },
  });

  const acceptDigitalPath = "project.contact.acceptDigital";
  const acceptDigitalField = bindField(acceptDigitalPath, {
    label: SUBSCRIPTION.FORM.CONSENTS?.PROSPECTION,
    onChange: () => setPercent(initialValues),
  });

  const acceptContactPath = "project.contact.acceptContact";
  const acceptContactField = bindField(acceptContactPath, {
    label: SUBSCRIPTION.FORM.CONSENTS?.PHONE_MARKETING,
    onChange: () => setPercent(initialValues),
  });

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.CONTACT}-section`}
    >
      <Form
        type="vertical"
        title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CONTACT.TITLE}
      >
        <Row gutter={[16, 16]}>
          <Col lg={24} xs={24}>
            <Form
              type="highlight"
              title={
                SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CONTACT.ADDRESS.TITLE
              }
            >
              <Row gutter={[16, 16]}>
                <Col lg={24} xs={24}>
                  <FormField
                    path={addressField.path}
                    label={addressField.label}
                    required={addressField.required}
                    disabled={addressField.disabled}
                  >
                    {(field) => (
                      <Input
                        label={addressField.label}
                        icon={<MdOutlineAddRoad size={16} />}
                        placeholder={
                          SUBSCRIPTION.FORM.STREET_AND_INDEX.PLACEHOLDER
                        }
                        type="custom"
                        normalizeText={true}
                        defaultValue={addressField.defaultValue}
                        {...field}
                        onChange={addressField.onChange}
                        onBlur={addressField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
                <Col lg={24} xs={24}>
                  <FormField
                    path={addressLocationField.path}
                    label={addressLocationField.label}
                    required={addressLocationField.required}
                    disabled={addressLocationField.disabled}
                  >
                    {(field) => (
                      <Input
                        label={addressLocationField.label}
                        optional={SUBSCRIPTION.FORM.OPTIONAL}
                        icon={<MdOutlineAddRoad size={16} />}
                        placeholder={
                          SUBSCRIPTION.FORM.ADDRESS_LOCATION.PLACEHOLDER
                        }
                        type="custom"
                        normalizeText={true}
                        defaultValue={addressLocationField.defaultValue}
                        {...field}
                        onChange={addressLocationField.onChange}
                        onBlur={addressLocationField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
                <Col lg={24} xs={24}>
                  <FormField
                    path={complementField.path}
                    label={complementField.label}
                    required={complementField.required}
                    disabled={complementField.disabled}
                  >
                    {(field) => (
                      <Input
                        label={complementField.label}
                        optional={SUBSCRIPTION.FORM.OPTIONAL}
                        icon={<MdOutlineAddRoad size={16} />}
                        placeholder={SUBSCRIPTION.FORM.COMPLEMENT.PLACEHOLDER}
                        type="custom"
                        normalizeText={true}
                        defaultValue={complementField.defaultValue}
                        {...field}
                        onChange={complementField.onChange}
                        onBlur={complementField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
                <Col lg={24} xs={24}>
                  <FormField
                    path={residenceField.path}
                    label={residenceField.label}
                    required={residenceField.required}
                    disabled={residenceField.disabled}
                  >
                    {(field) => (
                      <Input
                        label={residenceField.label}
                        optional={SUBSCRIPTION.FORM.OPTIONAL}
                        icon={<MdOutlineAddRoad size={16} />}
                        placeholder={SUBSCRIPTION.FORM.RESIDENCE.PLACEHOLDER}
                        type="custom"
                        normalizeText={true}
                        defaultValue={residenceField.defaultValue}
                        {...field}
                        onChange={residenceField.onChange}
                        onBlur={residenceField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
                <Col lg={24} xs={24}>
                  <FormField
                    path={zipCodeField.path}
                    label={zipCodeField.label}
                    required={zipCodeField.required}
                    disabled={zipCodeField.disabled}
                  >
                    {(field) => (
                      <Input
                        label={zipCodeField.label}
                        icon={<MdOutlinePinDrop size={16} />}
                        placeholder={SUBSCRIPTION.FORM.POSTAL_CODE.PLACEHOLDER}
                        defaultValue={zipCode}
                        maxLength={ACORIS_ZIP_CODE_LENGTH}
                        inputMode="numeric"
                        {...field}
                        disabled={zipCodeField.disabled}
                        onChange={zipCodeField.onChange}
                        onBlur={zipCodeField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
                <Col lg={24} xs={24}>
                  <FormField
                    path={cityField.path}
                    label={cityField.label}
                    required={cityField.required}
                    disabled={cityField.disabled}
                  >
                    {(field) =>
                      isForeignZip ? (
                        <Input
                          key="city-free"
                          label={cityField.label}
                          icon={<MdOutlineAddRoad size={16} />}
                          placeholder={getAcorisCityPlaceholder(
                            SUBSCRIPTION.FORM.CITY,
                            true,
                          )}
                          type="custom"
                          normalizeText={true}
                          defaultValue={cityField.defaultValue}
                          {...field}
                          onChange={cityField.onChange}
                          onBlur={cityField.onBlur}
                        />
                      ) : (
                        <Select
                          key="city-select"
                          icon={<MdOutlineSouthAmerica size={16} />}
                          label={cityField.label}
                          placeholder={SUBSCRIPTION.FORM.CITY.PLACEHOLDER}
                          showSearch
                          loading={citiesLoading}
                          options={cities}
                          defaultValue={cityField.defaultValue}
                          {...field}
                          onChange={cityField.onChange}
                          onBlur={cityField.onBlur}
                        />
                      )
                    }
                  </FormField>
                </Col>
                {isForeignZip && (
                  <Col lg={24} xs={24}>
                    <FormField
                      path={countryField.path}
                      label={countryField.label}
                      required={countryField.required}
                      disabled={countryField.disabled}
                    >
                      {(field) => (
                        <Select
                          icon={<MdOutlineSouthAmerica size={16} />}
                          label={countryField.label}
                          placeholder={SUBSCRIPTION.FORM.COUNTRY.PLACEHOLDER}
                          showSearch
                          options={countryOptions}
                          defaultValue={get(initialValues, countryPath)}
                          {...field}
                          onChange={countryField.onChange}
                          onBlur={countryField.onBlur}
                        />
                      )}
                    </FormField>
                  </Col>
                )}
              </Row>
            </Form>
          </Col>
          <Col lg={24} xs={24}>
            <Form
              type="highlight"
              title={
                SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CONTACT.BIRTH_PLACE
                  .TITLE
              }
            >
              <Row gutter={[16, 16]}>
                <Col lg={24} xs={24}>
                  <FormField
                    path={birthCountryField.path}
                    label={birthCountryField.label}
                    required={birthCountryField.required}
                    disabled={birthCountryField.disabled}
                  >
                    {(field) => (
                      <Select
                        label={birthCountryField.label}
                        icon={<MdOutlineSouthAmerica size={16} />}
                        placeholder={
                          SUBSCRIPTION.FORM.BIRTH_COUNTRY.PLACEHOLDER
                        }
                        options={countryOptions}
                        showSearch
                        autoComplete="off"
                        defaultValue={birthCountryField.defaultValue}
                        {...field}
                        onChange={birthCountryField.onChange}
                        onBlur={birthCountryField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
                <Col lg={24} xs={24}>
                  <FormField
                    path={birthCityField.path}
                    label={birthCityField.label}
                    required={birthCityField.required}
                    disabled={birthCityField.disabled}
                  >
                    {(field) => (
                      <Input
                        label={birthCityField.label}
                        icon={<MdOutlineAddRoad size={16} />}
                        placeholder={SUBSCRIPTION.FORM.BIRTH_CITY.PLACEHOLDER}
                        type="custom"
                        normalizeText={true}
                        autoComplete="off"
                        defaultValue={birthCityField.defaultValue}
                        {...field}
                        onChange={birthCityField.onChange}
                        onBlur={birthCityField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
              </Row>
            </Form>
          </Col>
          <Col lg={24} xs={24}>
            <Form
              type="highlight"
              title={
                SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CONTACT.PHONE.TITLE
              }
            >
              <Row gutter={[16, 16]}>
                <Col lg={24} xs={24}>
                  <Alert
                    showIcon
                    type="info"
                    message={
                      SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CONTACT.PHONE
                        .INFO
                    }
                  />
                </Col>
                <Col lg={24} xs={24}>
                  <FormField
                    path={phoneField.path}
                    label={phoneField.label}
                    required={phoneField.required}
                    disabled={phoneField.disabled}
                  >
                    {(field) => (
                      <Input
                        label={phoneField.label}
                        icon={<MdOutlineCall size={16} />}
                        placeholder={SUBSCRIPTION.FORM.MOBILE.PLACEHOLDER}
                        type="custom"
                        trim={true}
                        inputMode="numeric"
                        maxLength={10}
                        defaultValue={phoneField.defaultValue}
                        {...field}
                        onChange={phoneField.onChange}
                        onBlur={phoneField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
                <Col lg={24} xs={24}>
                  <FormField
                    path={landlineField.path}
                    label={landlineField.label}
                    required={landlineField.required}
                    disabled={landlineField.disabled}
                  >
                    {(field) => (
                      <Input
                        label={landlineField.label}
                        optional={SUBSCRIPTION.FORM.OPTIONAL}
                        icon={<MdOutlineCall size={16} />}
                        placeholder={SUBSCRIPTION.FORM.LANDLINE.PLACEHOLDER}
                        type="custom"
                        trim={true}
                        inputMode="numeric"
                        maxLength={10}
                        defaultValue={landlineField.defaultValue}
                        {...field}
                        onChange={landlineField.onChange}
                        onBlur={landlineField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
              </Row>
            </Form>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={emailField.path}
              label={emailField.label}
              required={emailField.required}
              disabled={emailField.disabled}
            >
              {(field) => (
                <Input
                  label={emailField.label}
                  icon={<MdOutlineAlternateEmail size={16} />}
                  placeholder={SUBSCRIPTION.FORM.EMAIL.PLACEHOLDER}
                  defaultValue={emailField.defaultValue}
                  {...field}
                  onChange={emailField.onChange}
                  onBlur={emailField.onBlur}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <Alert
              showIcon
              type="warning"
              message={
                SUBSCRIPTION.FORM.EMAIL.INFO_ACORIS ||
                "Cette adresse e-mail sera utilisée pour la signature électronique et la communication des documents contractuels. Veuillez vérifier qu'elle est valide et consultée régulièrement."
              }
            />
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={teletransmissionField.path}
              label={teletransmissionField.label}
              required={teletransmissionField.required}
              disabled={teletransmissionField.disabled}
            >
              {(field) => (
                <Input
                  type="switch"
                  label={teletransmissionField.label}
                  defaultChecked={
                    get(initialValues, teletransmissionPath, true) === false
                  }
                  {...field}
                  onChange={teletransmissionField.onChange}
                  onBlur={teletransmissionField.onBlur}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <Form
              type="highlight"
              title={SUBSCRIPTION.FORM.CONSENTS?.TITLE || "Consentements"}
            >
              <Row gutter={[16, 16]}>
                <Col lg={24} xs={24}>
                  <FormField
                    path={acceptDigitalField.path}
                    label={acceptDigitalField.label}
                    required={acceptDigitalField.required}
                    disabled={acceptDigitalField.disabled}
                  >
                    {(field) => (
                      <Input
                        type="switch"
                        label={acceptDigitalField.label}
                        checked={get(initialValues, acceptDigitalPath) === true}
                        {...field}
                        onChange={acceptDigitalField.onChange}
                        onBlur={acceptDigitalField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
                <Col lg={24} xs={24}>
                  <FormField
                    path={acceptContactField.path}
                    label={acceptContactField.label}
                    required={acceptContactField.required}
                    disabled={acceptContactField.disabled}
                  >
                    {(field) => (
                      <Input
                        type="switch"
                        label={acceptContactField.label}
                        checked={get(initialValues, acceptContactPath) === true}
                        {...field}
                        onChange={acceptContactField.onChange}
                        onBlur={acceptContactField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default Contact;
